// 작업 공간(테넌트) API — 백엔드 kr.taplink.tenant.TenantController (IMP-5)
// 타입은 OpenAPI 계약에서 생성(DSN-3)
import type { Schemas } from "@taplink/api-client";
import { request } from "@taplink/web-shared";

export type TenantSummary = Schemas["TenantSummary"];
export type TenantMember = Schemas["TenantMember"];
export type InvitationItem = Schemas["InvitationItem"];
export type TransferItem = Schemas["TransferItem"];
export type TransferResult = Schemas["TransferResult"];
export type ClosurePreflight = Schemas["ClosurePreflight"];
export type ClosureResult = Schemas["ClosureResult"];

export const tenantApi = {
  /** 내가 속한 작업 공간. 2개 이상일 때만 전환 UI를 띄운다(설계 §8) */
  list: () => request<TenantSummary[]>("/tenants"),

  /** 전환하면 **새 토큰**을 받는다 — 테넌트가 JWT 클레임에 있기 때문이다 */
  switch: (tenantId: number) =>
    request<{ accessToken: string; expiresIn: number }>("/tenants/switch", {
      method: "POST",
      body: { tenantId },
    }),

  create: (name: string) => request<TenantSummary>("/tenants", { method: "POST", body: { name } }),

  members: () => request<TenantMember[]>("/tenants/current/members"),

  changeRole: (accountId: number, role: TenantMember["role"]) =>
    request<void>(`/tenants/current/members/${accountId}`, { method: "PATCH", body: { role } }),

  removeMember: (accountId: number) =>
    request<void>(`/tenants/current/members/${accountId}`, { method: "DELETE" }),

  invitations: () => request<InvitationItem[]>("/tenants/current/invitations"),

  /**
   * @param tenantId 초대할 조직. 비우면 현재 작업 공간이다.
   *   소유자만 다른 조직을 지정할 수 있다 — 관리자는 자기 조직에만 초대한다.
   */
  invite: (email: string, role: TenantMember["role"], tenantId?: number) =>
    request<{ message: string }>("/tenants/current/invitations", {
      method: "POST",
      // 지정이 없으면 키를 **넣지 않는다** — 계약이 nullable 이지만 굳이 보낼 이유가 없다
      body: tenantId == null ? { email, role } : { email, role, tenantId },
    }),

  revokeInvitation: (invitationId: number) =>
    request<void>(`/tenants/current/invitations/${invitationId}`, { method: "DELETE" }),

  acceptInvitation: (token: string) =>
    request<TenantSummary>("/tenants/invitations/accept", { method: "POST", body: { token } }),

  // ── 소유권 이전 (DSN-19 §4) ───────────────────────────────────────

  nominateOwner: (accountId: number) =>
    request<TransferItem>("/tenants/current/ownership-transfer", { method: "POST", body: { accountId } }),

  /** 이 조직의 살아 있는 이전 요청. 없으면 서버가 204 → null */
  currentTransfer: () => request<TransferItem | null>("/tenants/current/ownership-transfer"),

  cancelTransfer: () => request<void>("/tenants/current/ownership-transfer", { method: "DELETE" }),

  /**
   * 나에게 온 이전 요청 — **작업 공간과 무관하다.**
   * 지목당한 사람이 그 조직으로 전환해 있을 이유가 없으므로 전역으로 묻는다.
   */
  incomingTransfers: () => request<TransferItem[]>("/tenants/ownership-transfers/incoming"),

  acceptTransfer: (transferId: number) =>
    request<TransferResult>(`/tenants/ownership-transfers/${transferId}/accept`, { method: "POST" }),

  declineTransfer: (transferId: number) =>
    request<void>(`/tenants/ownership-transfers/${transferId}/decline`, { method: "POST" }),

  // ── 접기·이름 변경 (DSN-19 §5) ────────────────────────────────────

  rename: (name: string) =>
    request<TenantSummary>("/tenants/current", { method: "PATCH", body: { name } }),

  /**
   * 개인 작업 공간을 조직으로 승격 — **링크를 옮기지 않는다.**
   * 같은 공간이 조직이 되므로 단축 URL·통계·QR 이 그대로 따라온다.
   * ⚠️ 되돌릴 수 없다: 초대하면 그때까지의 개인 링크가 그들에게 보인다.
   */
  promote: (name: string) =>
    request<TenantSummary>("/tenants/current/promote", { method: "POST", body: { name } }),

  /** 접기 전 점검 — 무엇이 막는지 **누르기 전에** 보여주기 위한 값들 */
  closurePreflight: () => request<ClosurePreflight>("/tenants/current/closure-preflight"),

  closeTenant: (targetTenantId: number | null) =>
    request<ClosureResult>("/tenants/current/closure", { method: "POST", body: { targetTenantId } }),
};

/**
 * 지금 보고 있는 작업 공간 — 액세스 토큰의 `tenantId` 클레임으로 찾는다.
 *
 * ⚠️ 파싱한 값을 **그대로 믿지 않는다.** 서버가 준 목록에서 그 id 를 찾아 쓰고,
 * 없으면 목록의 첫 항목으로 떨어진다. 권한 판정은 서버가 매 요청마다 다시 한다 —
 * 이 값은 "무엇을 보여줄까"에만 쓴다.
 *
 * 전환 UI 와 초대 폼이 함께 쓴다. 예전에는 전환 UI 안에 사문으로 있어, 초대 폼이
 * 현재 공간을 알 방법이 없었다 — 그래서 초대가 어디로 가는지 화면에 적을 수 없었고,
 * **개인 공간에 있는 채로 초대가 나갔다**(실측 운영 2026-08-24).
 */
export function activeTenantId(tenants: TenantSummary[]): number | undefined {
  const raw = sessionStorage.getItem("taplink.accessToken");
  if (!raw) return tenants[0]?.id;
  try {
    const payload = raw.split(".")[1];
    if (!payload) return tenants[0]?.id;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
      tenantId?: number | string;
    };
    const id = Number(claims.tenantId);
    return Number.isFinite(id) ? id : tenants[0]?.id;
  } catch {
    return tenants[0]?.id;
  }
}
